import { nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useAuthStore, { isUserToken } from '@/stores/authStore'
import { useAuth } from './useAuth'
import { idbQueryStorage, queryClient } from '../plugins/vue-query'
import { tokenValidationGuards, validateRouteGuards } from '@/router/guards'

/**
 * Sets up global auth-related side effects (watchers).
 * Call this ONCE in App.vue - not in every component.
 */
export function useAuthEffects() {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const { selectedToken } = useAuth()

  // Redirect to "Login" on implicit logout, token invalidation or session expiration. Keyed on token
  // presence (not isAuthenticated) so pre-auth flows (e.g. onboarding) also redirect when their token clears.
  watch(
    () => selectedToken.value != null,
    async (hasToken) => {
      if (!hasToken && !route.meta.allowUnauthorized && route.name !== 'Login') {
        if (authStore.isSessionExpired) {
          authStore.clearAllTokens()
        }

        await queryClient.cancelQueries()
        await idbQueryStorage.clear()
        queryClient.clear()

        void router.push({
          name: 'Login',
          query: route.fullPath !== '/' ? { 'return-to': encodeURIComponent(route.fullPath) } : {},
        })
      }
    }
  )

  // Invalidate all queries when switching users
  watch(
    () => (isUserToken(selectedToken.value?.payload) ? selectedToken.value.payload.u_id : null),
    async (userId, oldUserId) => {
      if (oldUserId != null && userId != null && userId !== oldUserId) {
        sessionStorage.removeItem('creditor-onboarding-canceled')

        await queryClient.cancelQueries()
        await idbQueryStorage.clear()

        await queryClient.invalidateQueries({
          queryKey: ['get', '/user--extended-data'],
        })

        const guardResult = await validateRouteGuards(route, tokenValidationGuards)

        // Flush useQuery watchers so observers' enabled-snapshot reflects the new token before refetch fires.
        await nextTick()

        // resetQueries (not invalidateQueries) — evict cached data so disabled observers don't keep showing stale values.
        void queryClient.resetQueries({
          predicate: (query) => query.queryKey[1] !== '/user--extended-data',
        })

        if (guardResult != null && typeof guardResult !== 'boolean') {
          void router.push(guardResult)
        }
      }
    }
  )
}
